import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import { useMoodTrackerState } from "./useMoodTrackerState";
import { formatDate } from "core/helpers/dates";
import { MoodActionType, UserMoodStatValue, UserStreakValue } from "shared/request/myHealthyAdvantageApi";
import { Mood } from "UIPalette/MoodTrackerCard/moodFactories";

export type MoodTrackerContextType = {
  moodAction: MoodActionType | null;
  moodStats?: UserMoodStatValue;
  streakData?: UserStreakValue;
  currentMood?: Mood;
  refreshMoodValues: VoidFunction;
  hasError: boolean;
  isLoading: boolean;
  setCurrentMoodValue: (mood: Mood) => void;
  clearCurrentMoodValue: VoidFunction;
  showStreakModal: boolean;
  handleCloseStreakModal: VoidFunction;
  showMoodJourneyModal: boolean;
  handleCloseMoodJourneyModal: VoidFunction;
  errorDisplay: string | null;
  triggerMutate: boolean;
  resetTriggerMutate: VoidFunction;
  handleErrorDisplay: (error: string | null) => void;
};

export const MoodTrackerContext = createContext({} as MoodTrackerContextType);

export const useMoodTrackerContext = () => useContext(MoodTrackerContext);

export function MoodTrackerContextProvider({ children }: PropsWithChildren) {
  const {
    moodStats,
    moodAction,
    streakData,
    refreshMoodValues,
    hasError,
    isLoading,
    setCurrentMoodValue,
    clearCurrentMoodValue,
    showStreakModal,
    handleCloseStreakModal,
    showMoodJourneyModal,
    handleCloseMoodJourneyModal,
    errorDisplay,
    triggerMutate,
    resetTriggerMutate,
    handleErrorDisplay,
  } = useMoodTrackerState();

  const contextValue = useMemo(
    () => ({
      moodStats,
      moodAction,
      streakData,
      currentMood: moodStats?.moods.find((mood) => formatDate(new Date(mood.createdDateUtc)) === formatDate(new Date()))?.mood,
      refreshMoodValues,
      hasError,
      isLoading,
      setCurrentMoodValue,
      clearCurrentMoodValue,
      showStreakModal,
      handleCloseStreakModal,
      showMoodJourneyModal,
      handleCloseMoodJourneyModal,
      errorDisplay,
      triggerMutate,
      resetTriggerMutate,
      handleErrorDisplay,
    }),
    [
      clearCurrentMoodValue,
      moodAction,
      errorDisplay,
      handleCloseStreakModal,
      handleCloseMoodJourneyModal,
      handleErrorDisplay,
      hasError,
      isLoading,
      moodStats,
      refreshMoodValues,
      resetTriggerMutate,
      setCurrentMoodValue,
      showStreakModal,
      showMoodJourneyModal,
      streakData,
      triggerMutate,
    ]
  );

  return <MoodTrackerContext.Provider value={contextValue}>{children}</MoodTrackerContext.Provider>;
}
